import React from 'react';

type EmailLogo = { src: string; alt?: string };

interface InterviewRequestEmailTemplateProps {
  intervieweeName: string;
  question: string;
  context?: string;
  submissionUrl: string;
  baseUrl?: string;
  personalizedMessage?: string;
  introText?: string;

  /** CID-id'er som sendes inn fra mail-senderen (attachments[].cid) */
  cidTopLogo?: string;
  cidFooterLeftLogo?: string;
  cidFooterRightLogo?: string;

  /** Valgfritt: eksplisitt logo-URLer (fallback hvis ingen CID) */
  logoTop?: EmailLogo;
  logoFooterLeft?: EmailLogo;
  logoFooterRight?: EmailLogo;
}

const ensureHttpsAbsolute = (url: string, baseHost = 'https://briefr.no') => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url.replace(/^http:\/\//i, 'https://');
  return `${baseHost.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
};

const InterviewRequestEmailTemplate: React.FC<InterviewRequestEmailTemplateProps> = ({
  intervieweeName,
  question,
  context = '',
  submissionUrl,
  baseUrl = 'https://briefr.no',
  personalizedMessage,
  introText,

  cidTopLogo,
  cidFooterLeftLogo,
  cidFooterRightLogo,

  // Fallback-URLer (må være offentlige, permanente)
  logoTop = {
    src: '/logos/briefr-logo-main.png',
    alt: 'Briefr'
  },
  logoFooterLeft = {
    src: '/logos/redaktoransvar-logo.png',
    alt: 'Redaktøransvar'
  },
  logoFooterRight = {
    src: '/logos/briefr-logo-main.png',
    alt: 'Briefr'
  },
}) => {
  const secureBase = ensureHttpsAbsolute(baseUrl);
  const secureSubmissionUrl = ensureHttpsAbsolute(submissionUrl);

  // Use hardcoded logo URLs since we removed LogoStorageService
  const svcTop = '';
  const svcFooterLeft = '';
  const svcFooterRight = '';

  // Prioritet: CID → hardkodet HTTPS PNG/JPG → service (kun hvis sikker)
  const topLogoSrc =
    (cidTopLogo ? `cid:${cidTopLogo}` : '') ||
    ensureHttpsAbsolute(logoTop.src, secureBase) ||
    'https://briefr.no/logos/briefr-logo-main.png' ||
    svcTop;

  const footerLeftLogoSrc =
    (cidFooterLeftLogo ? `cid:${cidFooterLeftLogo}` : '') ||
    ensureHttpsAbsolute(logoFooterLeft.src, secureBase) ||
    'https://briefr.no/logos/redaktoransvar-logo.png' ||
    svcFooterLeft;

  const footerRightLogoSrc =
    (cidFooterRightLogo ? `cid:${cidFooterRightLogo}` : '') ||
    ensureHttpsAbsolute(logoFooterRight.src, secureBase) ||
    'https://briefr.no/logos/briefr-logo-main.png' ||
    svcFooterRight;

  const displayContext = context.trim() ? context : '(ikke lagt til, fremstår åpenbar)';

  return (
    <div style={{ fontFamily:'Arial, sans-serif', maxWidth:600, margin:'0 auto', color:'#333', lineHeight:1.5 }}>
      {/* TOPP-LOGO */}
      <div style={{ textAlign:'center', margin:'20px 0' }}>
        <img
          src={topLogoSrc}
          alt={logoTop.alt || 'Briefr Logo'}
          width={56} height={56}
          style={{ width:'56px', height:'56px', display:'block', margin:'0 auto', border:0 }}
        />
      </div>

      <div style={{ padding:'0 20px' }}>
        <p>Hei {intervieweeName},</p>

        {personalizedMessage?.trim() && <p>{personalizedMessage}</p>}

        <p>
          {introText || "I juni 2025 lanseres Briefr.no, en ny formidler av nyheter, aktualitet og bredt samfunnsliv, med særlig fokus på formidling fra fageksperter. Vårt format tillater lengre og grundigere resonnementer; ditt svar publiseres i utgangspunktet ordrett, uavhengig av lengde, etter korrektursjekk. Vi gir deg beskjed dersom vi mot formodning finner det aktuelt å foreta endringer. Du mottar et varsel på epost når artikkelen er publisert."}
        </p>

        <p>Vi ønsker gjerne ditt svar på spørsmålet:</p>

        <div style={{ margin:'20px 0', borderLeft:'4px solid #ea384c', paddingLeft:'15px' }}>
          <p><strong>Spørsmål:</strong> {question}</p>
          <p><strong>Kontekst:</strong> {displayContext}</p>
        </div>

        <p>Vennligst trykk på knappen under for å gå til siden for innsending på våre nettsider.</p>

        <div style={{ margin:'30px 0', textAlign:'center' }}>
          <a
            href={secureSubmissionUrl}
            style={{
              backgroundColor:'#ea384c',
              color:'#fff',
              padding:'12px 30px',
              textDecoration:'none',
              borderRadius:'4px',
              fontWeight:'bold',
              display:'inline-block',
              textAlign:'center'
            }}
          >
            Svar her
          </a>
        </div>

        <p>Svar gjerne på denne eposten dersom du har spørsmål. Briefr driftes etter Vær Varsom-plakaten og Redaktørplakaten.</p>

        {/* SIGNATUR + FOOTER-LOGOER */}
        <div style={{ marginTop:'30px', borderTop:'1px solid #eee', paddingTop:'15px', fontSize:14 }}>
          <p>
            Vennlig hilsen,<br/>
            Redaksjonen | <a href="https://briefr.no" style={{ color:'inherit', textDecoration:'none' }}>briefr.no</a><br/>
            E-post: <a href="mailto:redaksjonen@briefr.no" style={{ color:'inherit' }}>redaksjonen@briefr.no</a><br/>
            Tlf: +47 97333909<br/>
            Ansvarlig redaktør: Tom Sekse
          </p>

          <div style={{ display:'flex', alignItems:'center', margin:'15px 0', gap:'20px', justifyContent:'center' }}>
            <img
              src={footerRightLogoSrc}
              alt={logoFooterRight.alt || 'Briefr Logo'}
              width={70} height={70}
              style={{ width:'70px', height:'70px', objectFit:'contain', display:'block', border:0 }}
            />
            <img
              src={footerLeftLogoSrc}
              alt="Redaktøransvar"
              width={70} height={70}
              style={{ width:'70px', height:'70px', objectFit:'contain', display:'block', border:0 }}
            />
          </div>

          <p><a href="https://www.briefr.no" style={{ color:'inherit', textDecoration:'none' }}>www.briefr.no</a></p>
        </div>
      </div>
    </div>
  );
};

export default InterviewRequestEmailTemplate;